// 顧客事前登録API - V3 API対応
// UNIFIED_DEVELOPMENT_GUIDE準拠 - TanStack Query v5統合

import { apiClient } from '@/services/api';
import type {
  CustomerPreRegistration,
  CustomerPreRegistrationListResponse,
  CustomerPreRegistrationStats,
  CustomerPreRegistrationFormInput,
  CustomerPreRegistrationFilters,
} from '../types';

// ========== API Endpoints ==========

const ENDPOINTS = {
  public: '/public/customer-pre-registrations',
  base: '/customer-pre-registrations',
  stats: '/customer-pre-registrations/stats',
  confirm: (id: number) => `/customer-pre-registrations/${id}/confirm`,
  convert: (id: number) => `/customer-pre-registrations/${id}/convert`,
  updateStatus: (id: number) => `/customer-pre-registrations/${id}/status`,
};

// ========== 顧客事前登録API ==========

export const customerPreRegistrationApi = {
  // ---------- Public API（認証不要） ----------

  // 公開フォーム登録
  storePublic: async (data: CustomerPreRegistrationFormInput): Promise<CustomerPreRegistration> => {
    const formData = new FormData();

    // 必須フィールド
    formData.append('store_id', data.store_id.toString());
    formData.append('family_name', data.family_name);
    formData.append('given_name', data.given_name);
    formData.append('family_name_kana', data.family_name_kana);
    formData.append('given_name_kana', data.given_name_kana);
    formData.append('birth_date', data.birth_date);
    formData.append('gender', data.gender);
    formData.append('mobile', data.mobile);
    formData.append('email', data.email);
    formData.append('postal_code', data.postal_code);
    formData.append('prefecture', data.prefecture);
    formData.append('city', data.city);
    formData.append('street_address', data.street_address);
    formData.append('confirmation', data.confirmation.toString());

    // オプショナルフィールド
    if (data.avatar) formData.append('avatar', data.avatar);
    if (data.phone) formData.append('phone', data.phone);
    if (data.building) formData.append('building', data.building);
    if (data.desired_program_id) formData.append('desired_program_id', data.desired_program_id.toString());
    if (data.desired_join_date) formData.append('desired_join_date', data.desired_join_date);
    if (data.desired_visit_date) formData.append('desired_visit_date', data.desired_visit_date);
    if (data.notes) formData.append('notes', data.notes);

    const response = await apiClient.post<{ data: CustomerPreRegistration }>(
      ENDPOINTS.public,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return response.data.data;
  },

  // ---------- Admin API（認証必須） ----------

  // 一覧取得
  getList: async (
    params?: CustomerPreRegistrationFilters & { page?: number; per_page?: number }
  ): Promise<CustomerPreRegistrationListResponse> => {
    const response = await apiClient.get<CustomerPreRegistrationListResponse>(
      ENDPOINTS.base,
      { params }
    );
    return response.data;
  },

  // 詳細取得
  getById: async (id: number): Promise<CustomerPreRegistration> => {
    const response = await apiClient.get<{ data: CustomerPreRegistration }>(
      `${ENDPOINTS.base}/${id}`
    );
    return response.data.data;
  },

  // 統計取得
  getStats: async (): Promise<CustomerPreRegistrationStats> => {
    const response = await apiClient.get<{ data: CustomerPreRegistrationStats }>(
      ENDPOINTS.stats
    );
    return response.data.data;
  },

  // 確認済みマーク
  confirm: async (id: number): Promise<CustomerPreRegistration> => {
    const response = await apiClient.patch<{ data: CustomerPreRegistration }>(
      ENDPOINTS.confirm(id)
    );
    return response.data.data;
  },

  // 正式顧客へ変換
  convert: async (id: number): Promise<CustomerPreRegistration> => {
    const response = await apiClient.post<{ data: CustomerPreRegistration }>(
      ENDPOINTS.convert(id)
    );
    return response.data.data;
  },

  // 削除
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${ENDPOINTS.base}/${id}`);
  },

  // ステータス更新
  updateStatus: async (id: number, data: { status: string; notes?: string }): Promise<CustomerPreRegistration> => {
    const response = await apiClient.patch<{ data: CustomerPreRegistration }>(
      ENDPOINTS.updateStatus(id),
      data
    );
    return response.data.data;
  },

  // 完全更新（管理画面用）
  update: async (id: number, data: CustomerPreRegistrationFormInput): Promise<CustomerPreRegistration> => {
    const formData = new FormData();

    // 必須フィールド
    formData.append('store_id', data.store_id.toString());
    formData.append('family_name', data.family_name);
    formData.append('given_name', data.given_name);
    formData.append('family_name_kana', data.family_name_kana);
    formData.append('given_name_kana', data.given_name_kana);
    formData.append('birth_date', data.birth_date);
    formData.append('gender', data.gender);
    formData.append('mobile', data.mobile);
    formData.append('email', data.email);
    formData.append('postal_code', data.postal_code);
    formData.append('prefecture', data.prefecture);
    formData.append('city', data.city);
    formData.append('street_address', data.street_address);

    // オプショナルフィールド
    if (data.avatar) formData.append('avatar', data.avatar);
    if (data.phone) formData.append('phone', data.phone);
    if (data.building) formData.append('building', data.building);
    if (data.desired_program_id) formData.append('desired_program_id', data.desired_program_id.toString());
    if (data.desired_join_date) formData.append('desired_join_date', data.desired_join_date);
    if (data.desired_visit_date) formData.append('desired_visit_date', data.desired_visit_date);
    if (data.notes) formData.append('notes', data.notes);

    // Laravel: FormDataでPUTメソッドを使うために _method を追加
    formData.append('_method', 'PUT');

    const response = await apiClient.post<{ data: CustomerPreRegistration }>(
      `${ENDPOINTS.base}/${id}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return response.data.data;
  },
};
